import React from 'react';
import { useVoiceRecording } from './useVoiceRecording';
import { formatDuration } from './audioUtils';

export interface VoiceRecorderProps {
  onRecordingComplete: (audioBlob: Blob) => void;
  onError?: (error: string) => void;
  maxDuration?: number; // seconds
  className?: string;
  disabled?: boolean;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onRecordingComplete,
  onError,
  maxDuration = 300, // 5 minutes default
  className = '',
  disabled = false
}) => {
  const {
    state,
    audioBlob,
    startRecording,
    stopRecording,
    resetRecording,
    isSupported
  } = useVoiceRecording();

  // Handle recording completion
  React.useEffect(() => {
    if (audioBlob && !state.isRecording) {
      onRecordingComplete(audioBlob);
    }
  }, [audioBlob, state.isRecording, onRecordingComplete]);

  // Handle errors
  React.useEffect(() => {
    if (state.error && onError) {
      onError(state.error);
    }
  }, [state.error, onError]);

  // Auto-stop at max duration
  React.useEffect(() => {
    if (state.isRecording && state.duration >= maxDuration) {
      stopRecording();
    }
  }, [state.isRecording, state.duration, maxDuration, stopRecording]);

  if (!isSupported) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center space-x-2 text-red-700">
          <span>🚫</span>
          <span className="text-sm font-medium">Audio recording not supported</span>
        </div>
        <p className="text-xs text-red-600 mt-1">
          Please use a modern browser with microphone support.
        </p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
      {/* Recording Tips - First */}
      {!state.isRecording && !audioBlob && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <h4 className="text-sm font-medium text-blue-900 mb-2">💡 Recording Tips</h4>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• Speak clearly and at a normal pace</li>
            <li>• Describe your template idea with industry context or purpose</li>
            <li>• Example: "Create a 401k consultation template for financial advisors"</li>
          </ul>
        </div>
      )}

      {/* Recording Status - Second */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            {state.isRecording ? (
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            ) : (
              <div className="w-3 h-3 bg-gray-300 rounded-full" />
            )}
            <span className="text-sm font-medium text-gray-700">
              {state.isRecording ? 'Recording...' : ''}
            </span>
          </div>
          
          {state.duration > 0 && (
            <span className="text-sm text-gray-500">
              {formatDuration(state.duration)}
            </span>
          )}
        </div>

        {maxDuration && (
          <span className="text-xs text-gray-400">
            Max: {formatDuration(maxDuration)}
          </span>
        )}
      </div>

      {/* Volume Visualization */}
      {state.isRecording && (
        <div className="mb-4">
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">Volume:</span>
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-100 ease-out"
                style={{ width: `${state.volume * 100}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 w-8">
              {Math.round(state.volume * 100)}%
            </span>
          </div>
        </div>
      )}

      {/* Error Display */}
      {state.error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded p-3">
          <div className="flex items-center space-x-2 text-red-700">
            <span>⚠️</span>
            <span className="text-sm font-medium">Recording Error</span>
          </div>
          <p className="text-xs text-red-600 mt-1">{state.error}</p>
        </div>
      )}

      {/* Control Buttons - Last */}
      <div className="space-y-3">
        {!state.isRecording ? (
          <button
            onClick={startRecording}
            disabled={disabled}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            <span>🎙️</span>
            <span>Start Recording</span>
          </button>
        ) : (
          <button
            onClick={stopRecording}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            <span>⏹️</span>
            <span>Stop Recording</span>
          </button>
        )}

        {(audioBlob || state.error) && !state.isRecording && (
          <button
            onClick={resetRecording}
            className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
          >
            <span>🔄</span>
            <span>Reset</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default VoiceRecorder; 